import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Calendar, Package, TrendingUp, Search, ChevronDown, ChevronRight } from 'lucide-react';

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function ReceptionsTab() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(daysAgoIso(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [search, setSearch] = useState('');
  const [expandedDate, setExpandedDate] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [articleDetails, setArticleDetails] = useState({}); // { ingredient: [entries] }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/inventaire/entrees/synthese', {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
    } catch (e) {
      toast.error('Erreur chargement synthèse réceptions');
    } finally {
      setLoading(false);
    }
  }, [api, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadArticleDetails = async (ingredient) => {
    if (articleDetails[ingredient]) return;
    try {
      const res = await api.get('/inventaire/entrees', {
        params: { date_from: dateFrom, date_to: dateTo, ingredient },
      });
      setArticleDetails(prev => ({ ...prev, [ingredient]: res.data }));
    } catch {
      toast.error('Erreur chargement détails');
    }
  };

  const parDate = data?.par_date || [];
  const parArticle = data?.par_article || [];

  const articlesFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parArticle;
    return parArticle.filter(a => (a.ingredient || '').toLowerCase().includes(q));
  }, [parArticle, search]);

  const datesFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parDate;
    return parDate.map(d => ({
      ...d,
      items: d.items.filter(it => (it.ingredient || '').toLowerCase().includes(q)),
    })).filter(d => d.items.length > 0);
  }, [parDate, search]);

  const setPreset = (days) => {
    setDateFrom(daysAgoIso(days));
    setDateTo(todayIso());
  };

  return (
    <div className="space-y-4" data-testid="receptions-tab">
      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Du</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="recep-date-from" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="recep-date-to" />
            </div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPreset(7)} data-testid="recep-preset-7">7j</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(30)} data-testid="recep-preset-30">30j</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(90)} data-testid="recep-preset-90">90j</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(365)} data-testid="recep-preset-365">1 an</Button>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Rechercher un article</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom de l'article…" className="pl-8" data-testid="recep-search" />
              </div>
            </div>
            <Button onClick={fetchData} variant="outline" size="sm" data-testid="recep-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Réceptions</div>
              <div className="text-2xl font-bold" data-testid="recep-stat-total">{data.total_entrees}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Articles distincts</div>
              <div className="text-2xl font-bold">{data.total_articles}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Jours avec réceptions</div>
              <div className="text-2xl font-bold">{data.total_dates}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Période</div>
              <div className="text-sm font-semibold">{fmtDate(data.date_from)} → {fmtDate(data.date_to)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="par_date">
          <TabsList>
            <TabsTrigger value="par_date" data-testid="recep-tab-date"><Calendar className="w-4 h-4 mr-1" /> Par date</TabsTrigger>
            <TabsTrigger value="par_article" data-testid="recep-tab-article"><Package className="w-4 h-4 mr-1" /> Par article</TabsTrigger>
          </TabsList>

          {/* Par date */}
          <TabsContent value="par_date">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Réceptions groupées par jour</CardTitle>
              </CardHeader>
              <CardContent>
                {datesFiltered.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-6">Aucune réception sur cette période.</div>
                ) : (
                  <div className="space-y-2">
                    {datesFiltered.map(d => {
                      const open = expandedDate === d.date;
                      return (
                        <div key={d.date} className="border rounded-md">
                          <button
                            onClick={() => setExpandedDate(open ? null : d.date)}
                            className="w-full flex items-center justify-between px-3 py-2 hover:bg-accent/50 transition"
                            data-testid={`recep-date-row-${d.date}`}
                          >
                            <div className="flex items-center gap-2">
                              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              <span className="font-medium">{fmtDate(d.date)}</span>
                              <Badge variant="secondary">{d.nb_articles} articles</Badge>
                              <Badge variant="outline">{d.nb_receptions} lignes</Badge>
                            </div>
                          </button>
                          {open && (
                            <div className="border-t bg-muted/20">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Article</TableHead>
                                    <TableHead>Zone</TableHead>
                                    <TableHead className="text-right">Quantité</TableHead>
                                    <TableHead>Unité</TableHead>
                                    <TableHead>Source</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {d.items.map((it, i) => (
                                    <TableRow key={i}>
                                      <TableCell className="font-medium">{it.ingredient}</TableCell>
                                      <TableCell><span className="text-xs text-muted-foreground">{it.zone}</span></TableCell>
                                      <TableCell className="text-right tabular-nums">+{it.quantite}</TableCell>
                                      <TableCell>{it.unite}</TableCell>
                                      <TableCell>
                                        <Badge variant="outline" className="text-xs">
                                          {it.source === 'stock_adjust' ? 'Ajout manuel' : (it.source || 'manuel')}
                                        </Badge>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Par article */}
          <TabsContent value="par_article">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Cumul par article
                </CardTitle>
              </CardHeader>
              <CardContent>
                {articlesFiltered.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-6">Aucun article sur cette période.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-6"></TableHead>
                        <TableHead>Article</TableHead>
                        <TableHead>Zone</TableHead>
                        <TableHead className="text-right">Qté totale reçue</TableHead>
                        <TableHead>Unité</TableHead>
                        <TableHead className="text-right">Nb réceptions</TableHead>
                        <TableHead>Première</TableHead>
                        <TableHead>Dernière</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {articlesFiltered.map(a => {
                        const open = expandedArticle === a.ingredient;
                        const details = articleDetails[a.ingredient] || [];
                        return (
                          <Fragment key={a.ingredient}>
                            <TableRow
                                      className="cursor-pointer hover:bg-accent/40"
                                      onClick={() => {
                                        const next = open ? null : a.ingredient;
                                        setExpandedArticle(next);
                                        if (next) loadArticleDetails(a.ingredient);
                                      }}
                                      data-testid={`recep-article-row-${a.ingredient}`}>
                              <TableCell>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                              <TableCell className="font-medium">{a.ingredient}</TableCell>
                              <TableCell><span className="text-xs text-muted-foreground">{a.zone}</span></TableCell>
                              <TableCell className="text-right tabular-nums font-semibold">{a.qte_totale}</TableCell>
                              <TableCell>{a.unite}</TableCell>
                              <TableCell className="text-right"><Badge variant="secondary">{a.nb_receptions}</Badge></TableCell>
                              <TableCell className="text-xs">{fmtDate(a.premiere_reception)}</TableCell>
                              <TableCell className="text-xs">{fmtDate(a.derniere_reception)}</TableCell>
                            </TableRow>
                            {open && (
                              <TableRow>
                                <TableCell colSpan={8} className="bg-muted/20 p-0">
                                  <div className="p-3">
                                    {details.length === 0 ? (
                                      <div className="text-xs text-muted-foreground py-2">Chargement…</div>
                                    ) : (
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead className="text-right">Quantité</TableHead>
                                            <TableHead>Source</TableHead>
                                            <TableHead>Utilisateur</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {details.map(e => (
                                            <TableRow key={e.id}>
                                              <TableCell className="text-xs">{fmtDate(e.date_reception)}</TableCell>
                                              <TableCell className="text-right tabular-nums">+{e.quantite} {e.unite}</TableCell>
                                              <TableCell><Badge variant="outline" className="text-xs">{e.source === 'stock_adjust' ? 'Ajout manuel' : (e.source || 'manuel')}</Badge></TableCell>
                                              <TableCell className="text-xs text-muted-foreground">{e.user_email || '—'}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
